import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/functions';
import 'firebase/storage';
import 'firebase/analytics';
import moment from 'moment';
import { getRoutePrefix } from 'utils/routes';

const config = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};


class FirebaseClient {
  constructor() {
    firebase.initializeApp(config);
    this.auth = firebase.auth();
    this.db = firebase.firestore();
    this.storage = firebase.storage();
    this.functions = firebase.functions();
    this.analytics = firebase.analytics();
    this.config = config;
  }

  signup = async (
    { email, password, displayName, firstName, lastName, role, inviteId },
    callback
  ) => {
    try {
      const authUser = await this.auth.createUserWithEmailAndPassword(
        email,
        password
      );

      if (!authUser.user) return;
      const { uid } = authUser.user;

      await this.functions.httpsCallable('UserRoles-setRole')({
        uid,
        email,
        role,
      });

      await this.functions.httpsCallable('UserRoles-addToSendGrid')({
        email,
        role,
        firstName,
        lastName,
        uid,
      });

      await authUser.user.getIdTokenResult(true);
      await this.setUserProfile({
        info: { role, firstName, lastName, email },
        disabled: false,
      });
      await authUser.user.updateProfile({
        displayName,
      });

      if (inviteId != null) {
        await this.functions.httpsCallable(
          'Organizations-acceptOganizationInvite'
        )({
          inviteId,
          userId: uid,
        });
      }

      // create url callback to dashboard based on user role
      let url = `${process.env.REACT_APP_CONFIRMATION_EMAIL_REDIRECT + getRoutePrefix(role)}/dashboard`;
      console.log('email confirmation url', url);

      this.sendEmailVerification(url);
      callback();
    } catch (err) {
      console.error('Error creating the user', err);
      throw err;
    }
  };

  currentUserId = () => {
    if (this.auth.currentUser == null) return null;
    return this.auth.currentUser.uid;
  };

  dbUser = (uid) => this.db.doc(`users/${uid}`);

  dbUsers = () => this.db.collection('users');

  dbProfilePath = () => {
    return `users/${this.currentUserId()}`;
  };

  dbDoc = (path) => this.db.doc(path);

  dbUserDoc = (path) => this.db.doc(`${this.dbProfilePath}${path}`);

  dbProfile = () => this.db.doc(this.dbProfilePath());

  dbOrganization = (uid) => this.db.doc(`organizations/${uid}`);

  dbOrganizations = () => this.db.collection('organizations');

  sendEmailVerification = async (url) => {
    await this.auth.currentUser.sendEmailVerification({
      url: `${url}`,
    });
  };

  passwordReset = (email) => this.auth.sendPasswordResetEmail(email);

  passwordUpdate = (password) => this.auth.currentUser.updatePassword(password);

  disableAccount = async (uid) => {
    try {
      return this.functions.httpsCallable('UserRoles-disableUserAccount')({
        uid,
      });
    } catch (e) {
      console.error(e);
      return e;
    }
  };

  enableAccount = async (uid) => {
    try {
      return this.functions.httpsCallable('UserRoles-enableUserAccount')({
        uid,
      });
    } catch (e) {
      console.error(e);
      return e;
    }
  };

  deleteAccount = async (uid) => {
    try {
      return this.functions.httpsCallable('UserRoles-deleteUserData')({
        uid,
      });
    } catch (e) {
      console.error(e);
      return e;
    }
  };

  setUserProfilePicture = async (photoURL) => {
    try {
      const authUser = this.auth.currentUser;
      await authUser.updateProfile({
        photoURL,
      });
    } catch (err) {
      console.error('Error updating user profile picture', err);
      throw err;
    }
  };

  setUserProfile = async (values) => {
    try {
      await this.dbProfile().set(
        {
          ...values,
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Error updating user profile', err);
      throw err;
    }
  };

  setUserProfileComplete = async (isProfileComplete) => {
    try {
      await this.dbProfile().set(
        {
          isProfileComplete,
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Error setting user profile as complete', err);
      throw err;
    }
  };

  sendDBSInfo = async (data) => {
    try {
      // check if the data is allowed to be sent first
      const { certificateDBS, info } = data;

      if (!info || !certificateDBS || !certificateDBS.permissionToCheckDBS) {
        throw new Error('DBS data has no permission to be sent to My-Shifts!');
      }

      const message = `A worker has allowed My-Shifts permission to check their DBS information:

      Name - ${info.firstName} ${info.lastName}
      DOB - ${moment.utc(info.dateOfBirth).format('DD/MM/YY')}
      Nationality - ${info.nationality}
      DBS No. - ${certificateDBS.id}
      DBS Certificate - ${certificateDBS.fileURL}`;

      await this.functions.httpsCallable('UserData-sendDBSInfo')({
        email: 'dbs@my-shifts.co.uk',
        message,
      });
    } catch (err) {
      console.error('DBS data has no permission to be sent to My-Shifts', err);
      throw err;
    }
  };

  addTokens = async (amount) => {
    try {
      const docRef = this.dbProfile();
      return this.db.runTransaction(async (transaction) => {
        const userDoc = await transaction.get(docRef);

        if (!userDoc.exists) {
          throw new Error('Document does not exist!');
        }

        const { tokens = 0 } = userDoc.data();
        const newTokens = tokens + amount;
        transaction.update(docRef, { tokens: newTokens });
      });
    } catch (error) {
      console.error('Error adding tokens to user');
      throw error;
    }
  };

  transferTokens = async ({ amount, to }) => {
    try {
      const data = {
        fromId: this.auth.currentUser.uid,
        toId: to,
        amount,
      };
      await this.functions.httpsCallable('Tokens-transferTokens')(data);
    } catch (err) {
      console.error('Error transfering tokens', err);
      throw err;
    }
  };

  addOrganization = async (values) => {
    try {
      const ref = await this.dbOrganizations().add({
        ...values,
        userId: this.auth.currentUser.uid,
      });
      return ref.id;
    } catch (err) {
      console.error('Error creating organisation', err);
      throw err;
    }
  };

  setOrganization = async (uid, values) => {
    try {
      if (uid == null) {
        return await this.addOrganization(values);
      }
      return await this.dbOrganization(uid).set(
        {
          ...values,
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Error updating organisation values organisation', err);
      throw err;
    }
  };

  inviteAdmin = async (data) => {
    try {
      await this.functions.httpsCallable(
        'Organizations-inviteOrganizationAdmin'
      )(data);
    } catch (err) {
      console.error('Error updating organisation values organisation', err);
      throw err;
    }
  };

  // TODO: rename to token_request
  requestTokens = async (data) => {
    try {
      const { amount } = data;
      const userId = this.currentUserId();

      return await this.db.collection('token_requests').add({
        amount,
        userId,
      });
    } catch (err) {
      console.error('Error requesting tokens', err);
      throw err;
    }
  };

  payToken = async (data) => {
    try {
      const { uid } = this.auth.currentUser;
      return await this.functions.httpsCallable('Stripe-createTokenPayment')({
        userId: uid,
        ...data,
      });
    } catch (err) {
      console.error('Error creating payment intents for token', err);
      throw err;
    }
  };

  connectWithWorker = async (data) => {
    try {
      const { worker } = data;
      const result = await this.functions.httpsCallable(
        'Booking-connectWithWorker'
      )({
        worker,
        employer: this.currentUserId(),
      });
      return result;
    } catch (err) {
      console.error('Error connecting with worker', err);
      throw err;
    }
  };

  bookWorker = async (data) => {
    try {
      const userId = this.currentUserId();

      return await this.db.collection('booking_request').add({
        userId,
        ...data,
      });
    } catch (err) {
      console.error('Error requesting booking', err);
      throw err;
    }
  };

  cancelBookingRequest = async (id) => {
    try {
      return await this.db.doc(`booking_request/${id}`).set(
        {
          status: 'cancelled',
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Error canceling booking', err);
      throw err;
    }
  };

  // TODO: Change contract to follow booking request
  acceptBookingRequest = async (data) => {
    const { employer, bookingRequest } = data;
    try {
      const worker = this.currentUserId();
      const allowedConnections = {};
      allowedConnections[employer] = true;

      // add to allowed connection list in the user data
      await this.dbProfile().set(
        {
          allowedConnections,
        },
        { merge: true }
      );

      return await this.functions.httpsCallable('Booking-acceptBookingRequest')(
        {
          worker,
          employer,
          bookingRequest,
        }
      );
    } catch (err) {
      console.error('Error requesting booking', err);
      throw err;
    }
  };

  // TODO: Insteadd of callable function make it a onUpdateDoc trigger
  rejectBookingRequest = async (data) => {
    const { id, userId } = data;
    try {
      return await this.functions.httpsCallable('Booking-rejectBookingRequest')(
        {
          userId,
          id,
        }
      );
    } catch (err) {
      console.error('Error requesting booking', err);
      throw err;
    }
  };

  upload = async ({ file, uploadTo, onProgress, onError, onSuccess }) => {
    const { storage } = this;
    try {
      const storageRef = await storage.ref();
      const uploadFile = storageRef.child(`${uploadTo}/${file.name}`);
      const uploadTask = uploadFile.put(file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Observe state change events such as progress, pause, and resume
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded

          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          ).toFixed(2);
          switch (snapshot.state) {
            // case firebase.storage.TaskState.PAUSED: // or 'paused'
            //   break;
            case firebase.storage.TaskState.RUNNING:
              onProgress({ percent }, file);
              break;
            default:
              break;
          }
        },
        (error) => {
          // Handle unsuccessful uploads
          onError(error, file);
        },
        () => {
          // Handle successful uploads on complete
          // For instance, get the download URL: https://firebasestorage.googleapis.com/...
          uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
            onSuccess(downloadURL, file);
          });
        }
      );
    } catch (e) {
      console.error(e);
      onError(e);
    }
  };
}

const Client = new FirebaseClient();

export default Client;
